import { useCallback, useEffect, useRef, useState } from 'react';

import { RootComponentInstance } from '@uniformdev/canvas';
import { UniformEntryPage } from 'src/pages/uniform-entry/uniform-entry-page';
import { useWAEContext } from 'src/uniform/utils/wae-context-provider';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { ProductKeys } from '@atlassiansox/asc-stargate';

import { CrossFlowTouchpointProvider } from '@/components/cross-flow/touchpoint/context';
import { useLoggedInHasJira } from '@/components/user-context/helpers/cohorts';
import { IntercomProvider } from '@/libraries/intercom/src/provider';
import { LoggedInHeroContainer } from '@/templates/software/jira/logged-in/organic-logged-in/logged-in-hero-container';
import { OrganicLoggedInJiraProductTourOverview } from '@/templates/software/jira/logged-in/organic-logged-in/organic-logged-in-jira-product-tour-overview.template';
import { useRecentJiraSite } from '@/templates/software/jira/logged-in/utils/use-recent-jira-site';
import { LoggedOutOrNoJiraProductTourOverview } from '@/templates/software/jira/logged-out/jira-software-logged-out-page/src/jira-software-logged-out-page.template';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment, EnrollmentType } from '@/utils/experiment/use-experiment';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { JiraSoftwarePageLoadingSpinner } from './components/jira-software-page-loading-spinner';

const JIRA_REDIRECT_DESTINATION_PATH = '/jira/for-you';
const JIRA_REDIRECT_SOURCE_PARAM = 'redirectSource';
const JIRA_REDIRECT_ANALYTICS_SUBJECT = 'loggedInJiraProductRedirect';
const JIRA_REDIRECT_EXPERIMENT_NAME = experiments.d1d6redirectLoggedInUsers.experimentName;
// Timeout threshold for site resolution — if it takes longer than this, we fire a timeout failure event
const SITE_RESOLUTION_TIMEOUT_MS = 5000;

export const buildJiraForYouUrl = (siteUrl?: string): string | undefined => {
	if (!siteUrl) {
		return undefined;
	}

	try {
		const jiraForYouUrl = new URL(
			`${JIRA_REDIRECT_DESTINATION_PATH}?tab=recommendedActions`,
			siteUrl,
		);
		jiraForYouUrl.searchParams.set(JIRA_REDIRECT_SOURCE_PARAM, JIRA_REDIRECT_EXPERIMENT_NAME);
		return jiraForYouUrl.href;
	} catch {
		return undefined;
	}
};

export const JiraSoftwarePage = () => {
	const loggedInHasJira = useLoggedInHasJira();
	const isWae = checkFeatureFlagGate(featureFlags.dxpMigrationJiraProductTourGate);

	// FYI, this is currently JA only, since we only have JA work space appId
	const isBootIntercom = checkFeatureFlagGate(featureFlags.gwp737EnableIntercomChatJsw);

	const redirectLoggedInUsersExperiment = useExperiment(experiments.d1d6redirectLoggedInUsers);
	const shouldRedirectLoggedInUsers =
		redirectLoggedInUsersExperiment.type === EnrollmentType.ENROLLED &&
		redirectLoggedInUsersExperiment.value;

	let template: React.ReactNode = <LoggedOutOrNoJiraProductTourOverview />;

	// For logged in, we will either redirect to Jira, show a "Has Jira" experience,
	// or otherwise the Uniform "logged out" page.
	if (loggedInHasJira) {
		template = shouldRedirectLoggedInUsers ? (
			<RedirectingLoggedInJiraPage />
		) : (
			<LoggedInHeroContainer>
				<OrganicLoggedInJiraProductTourOverview />
			</LoggedInHeroContainer>
		);
	} else if (isWae) {
		template = <WaeJiraPage />;
	}

	return (
		<UFOSegment name="jira-product-tour-overview">
			<IntercomProvider appId="p0ww63i2" triggerBoot={isBootIntercom}>
				<CrossFlowTouchpointProvider
					productKeys={[ProductKeys.JIRA_SOFTWARE]}
					screenName="wacJswProductTourPage"
				>
					{template}
				</CrossFlowTouchpointProvider>
			</IntercomProvider>
		</UFOSegment>
	);
};

const RedirectingLoggedInJiraPage = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { siteUrl, isResolvingSite } = useRecentJiraSite();
	const [redirectAssignFailed, setRedirectAssignFailed] = useState(false);
	const hasTrackedAttemptRef = useRef(false);
	const hasTrackedFailureRef = useRef(false);
	const loadingStartTimeRef = useRef<number | null>(null);
	const jiraForYouUrl = buildJiraForYouUrl(siteUrl);

	const fireRedirectAnalyticsEvent = useCallback(
		({
			action,
			failureReason,
			errorMessage,
			durationMs,
		}: {
			action: 'initiated' | 'failed' | 'loading_started' | 'loading_completed';
			failureReason?:
				| 'missing_redirect_target'
				| 'redirect_assign_failed'
				| 'site_resolution_timeout';
			errorMessage?: string;
			durationMs?: number;
		}) => {
			// All events use 'track' so they are forwarded to Statsig/Socrates for experiment analysis
			createAnalyticsEvent({
				eventType: 'track',
				action,
				actionSubject: JIRA_REDIRECT_ANALYTICS_SUBJECT,
				actionSubjectId: JIRA_REDIRECT_EXPERIMENT_NAME,
				source: '/software/jira',
				attributes: {
					experimentName: JIRA_REDIRECT_EXPERIMENT_NAME,
					redirectSource: JIRA_REDIRECT_EXPERIMENT_NAME,
					redirectDestinationPath: JIRA_REDIRECT_DESTINATION_PATH,
					failureReason,
					errorMessage,
					durationMs,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	// Track when loading starts and set up a timeout to detect hangs
	useEffect(() => {
		if (!isResolvingSite) {
			return;
		}

		loadingStartTimeRef.current = Date.now();
		fireRedirectAnalyticsEvent({
			action: 'loading_started',
		});

		const timeoutId = setTimeout(() => {
			if (!hasTrackedFailureRef.current) {
				hasTrackedFailureRef.current = true;
				// Use actual elapsed time; fall back to the threshold constant if start time was lost
				const elapsed =
					loadingStartTimeRef.current !== null
						? Date.now() - loadingStartTimeRef.current
						: SITE_RESOLUTION_TIMEOUT_MS;
				fireRedirectAnalyticsEvent({
					action: 'failed',
					failureReason: 'site_resolution_timeout',
					durationMs: elapsed,
				});
			}
		}, SITE_RESOLUTION_TIMEOUT_MS);

		return () => clearTimeout(timeoutId);
	}, [isResolvingSite, fireRedirectAnalyticsEvent]);

	// Track when loading completes and how long it took
	useEffect(() => {
		if (isResolvingSite || loadingStartTimeRef.current === null) {
			return;
		}

		const durationMs = Date.now() - loadingStartTimeRef.current;
		loadingStartTimeRef.current = null;
		fireRedirectAnalyticsEvent({
			action: 'loading_completed',
			durationMs,
		});
	}, [isResolvingSite, fireRedirectAnalyticsEvent]);

	useEffect(() => {
		if (!jiraForYouUrl || redirectAssignFailed || hasTrackedAttemptRef.current) {
			return;
		}

		hasTrackedAttemptRef.current = true;
		fireRedirectAnalyticsEvent({
			action: 'initiated',
		});

		try {
			window.location.assign(jiraForYouUrl);
		} catch (error) {
			setRedirectAssignFailed(true);

			if (!hasTrackedFailureRef.current) {
				hasTrackedFailureRef.current = true;
				fireRedirectAnalyticsEvent({
					action: 'failed',
					failureReason: 'redirect_assign_failed',
					errorMessage: error instanceof Error ? error.message : String(error),
				});
			}
		}
	}, [jiraForYouUrl, redirectAssignFailed, fireRedirectAnalyticsEvent]);

	useEffect(() => {
		if (isResolvingSite || jiraForYouUrl || hasTrackedFailureRef.current) {
			return;
		}

		hasTrackedFailureRef.current = true;
		fireRedirectAnalyticsEvent({
			action: 'failed',
			failureReason: 'missing_redirect_target',
		});
	}, [isResolvingSite, jiraForYouUrl, fireRedirectAnalyticsEvent]);

	if (isResolvingSite) {
		return <JiraSoftwarePageLoadingSpinner />;
	}

	return jiraForYouUrl && !redirectAssignFailed ? null : (
		<LoggedInHeroContainer>
			<OrganicLoggedInJiraProductTourOverview />
		</LoggedInHeroContainer>
	);
};

const WaeJiraPage = () => {
	const { composition } = useWAEContext();

	return <UniformEntryPage composition={composition as RootComponentInstance} />;
};
